import { useRef, useState } from "react";
import { toast } from "sonner";
import {
  ArrowLeft, UploadCloud, FileText, Loader2, RotateCcw, ListChecks,
  Sparkles, CheckCircle2, FileWarning,
} from "lucide-react";
import { api } from "@/api/client";

const ACCEPT = ".jpg,.jpeg,.png,.webp,.heic,.gif,.pdf,.txt";
const MAX_MB = 10;

const TYPE_STYLE = {
  receipt: "bg-emerald-500", invoice: "bg-emerald-500", bill: "bg-amber-500",
  contract: "bg-rose-500", statement: "bg-blue-500", other: "bg-charcoal",
};

export default function DocReader({ onClose }) {
  const [status, setStatus] = useState("idle"); // idle | reading | done | error
  const [result, setResult] = useState(null);
  const [errMsg, setErrMsg] = useState("");
  const [fileName, setFileName] = useState("");
  const [drag, setDrag] = useState(false);
  const inputRef = useRef(null);

  const reset = () => {
    setStatus("idle"); setResult(null); setErrMsg(""); setFileName("");
  };

  const handleFile = async (file) => {
    if (!file) return;
    if (file.size > MAX_MB * 1024 * 1024) {
      toast.error(`File too large (max ${MAX_MB} MB)`);
      return;
    }
    setFileName(file.name);
    setStatus("reading");
    setErrMsg("");
    setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data: up } = await api.post("/alix/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const { data } = await api.post("/alix/doc-read", { file_id: up.file_id, name: up.name });
      setResult(data);
      setStatus("done");
    } catch (e) {
      const msg = e?.response?.data?.detail || "Couldn't read that document. Please try again.";
      setErrMsg(msg);
      setStatus("error");
    }
  };

  const onDrop = (e) => {
    e.preventDefault(); setDrag(false);
    const f = e.dataTransfer?.files?.[0];
    if (f) handleFile(f);
  };

  return (
    <div className="h-full w-full max-w-[412px] mx-auto flex flex-col overflow-hidden bg-charcoal" data-testid="doc-reader">
      {/* Header */}
      <header className="shrink-0 flex items-center justify-between px-3 py-3 border-b-2 border-gold/40">
        <button onClick={onClose} data-testid="doc-reader-close" aria-label="Close"
          className="w-9 h-9 grid place-items-center border-2 border-gold bg-charcoal text-gold shadow-[2px_2px_0px_#D4AF37] active:translate-x-[1px] active:translate-y-[1px] active:shadow-none">
          <ArrowLeft size={17} strokeWidth={2.6} />
        </button>
        <div className="flex items-center gap-2">
          <FileText size={18} className="text-gold" />
          <span className="font-mono text-lg font-black tracking-[0.22em] text-gold" data-testid="doc-reader-title">DOC READER</span>
        </div>
        <span className="w-9" />
      </header>

      <div className="flex-1 min-h-0 overflow-y-auto no-scrollbar px-4 py-4">
        {/* IDLE — dropzone */}
        {status === "idle" && (
          <>
            <p className="font-mono text-[12px] text-white/70 leading-relaxed mb-4">
              Drop a <span className="text-gold font-bold">receipt, invoice, bill or contract</span> and ALIX explains it in plain English — key amounts, dates, obligations and what to do next.
            </p>
            <button
              type="button"
              data-testid="doc-reader-dropzone"
              onClick={() => inputRef.current?.click()}
              onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
              onDragLeave={() => setDrag(false)}
              onDrop={onDrop}
              className={`w-full grid place-items-center gap-3 rounded-2xl border-4 border-dashed py-14 px-4 transition-colors ${drag ? "border-gold bg-gold/10" : "border-white/25 bg-white/5"}`}
            >
              <UploadCloud size={40} className="text-gold" />
              <span className="font-black text-cream text-sm uppercase tracking-wide">Tap or drop a file</span>
              <span className="font-mono text-[10px] text-white/50 uppercase tracking-widest">Image · PDF · TXT · max {MAX_MB}MB</span>
            </button>
            <input ref={inputRef} type="file" accept={ACCEPT} className="hidden"
              data-testid="doc-reader-file-input"
              onChange={(e) => handleFile(e.target.files?.[0])} />
          </>
        )}

        {/* READING */}
        {status === "reading" && (
          <div className="mt-16 flex flex-col items-center text-center" data-testid="doc-reader-loading">
            <Loader2 size={40} className="text-gold animate-spin" />
            <p className="mt-4 font-black text-cream text-sm">Reading your document…</p>
            <p className="mt-1 font-mono text-[11px] text-white/50 truncate max-w-[240px]">{fileName}</p>
          </div>
        )}

        {/* ERROR */}
        {status === "error" && (
          <div className="mt-14 flex flex-col items-center text-center" data-testid="doc-reader-error">
            <FileWarning size={40} className="text-rose-400" />
            <p className="mt-4 font-mono text-[12px] text-white/80 leading-relaxed max-w-[280px]">{errMsg}</p>
            <button onClick={reset} data-testid="doc-reader-retry"
              className="mt-5 flex items-center gap-2 bg-gold text-black border-2 border-black rounded-xl px-4 py-2.5 font-black text-[11px] uppercase tracking-widest shadow-[3px_3px_0px_#000] active:translate-x-[2px] active:translate-y-[2px] active:shadow-none">
              <RotateCcw size={14} /> Try another
            </button>
          </div>
        )}

        {/* DONE — result */}
        {status === "done" && result && (
          <div className="space-y-3" data-testid="doc-reader-result">
            <div className="flex items-center justify-between">
              <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 font-mono text-[9px] font-black uppercase tracking-widest text-white ${TYPE_STYLE[result.doc_type] || TYPE_STYLE.other}`}>
                {result.doc_type}
              </span>
              <button onClick={reset} data-testid="doc-reader-reset"
                className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest text-gold">
                <RotateCcw size={13} /> New
              </button>
            </div>

            <h2 className="font-serif font-black text-cream text-2xl leading-tight" data-testid="doc-reader-doctitle">{result.title}</h2>

            <div className="bg-cream text-charcoal border-2 border-charcoal rounded-2xl p-3.5 shadow-[3px_3px_0px_#D4AF37]">
              <div className="flex items-center gap-1.5 mb-1.5">
                <Sparkles size={14} className="text-charcoal" />
                <span className="font-mono text-[9px] font-black uppercase tracking-widest text-charcoal/60">Plain English</span>
              </div>
              <p className="font-mono text-[13px] leading-relaxed" data-testid="doc-reader-summary">{result.summary}</p>
            </div>

            {result.fields?.length > 0 && (
              <div className="bg-white/5 border-2 border-white/15 rounded-2xl p-3.5" data-testid="doc-reader-fields">
                {result.fields.map((f, i) => (
                  <div key={i} className={`flex items-start justify-between gap-3 py-1.5 ${i ? "border-t border-white/10" : ""}`}>
                    <span className="font-mono text-[10px] uppercase tracking-wide text-white/50 shrink-0">{f.label}</span>
                    <span className="font-mono text-[12px] text-cream text-right font-bold">{f.value}</span>
                  </div>
                ))}
              </div>
            )}

            {result.key_points?.length > 0 && (
              <div className="bg-white/5 border-2 border-white/15 rounded-2xl p-3.5" data-testid="doc-reader-keypoints">
                <div className="flex items-center gap-1.5 mb-2">
                  <ListChecks size={14} className="text-gold" />
                  <span className="font-mono text-[9px] font-black uppercase tracking-widest text-gold">Key points</span>
                </div>
                <ul className="space-y-2">
                  {result.key_points.map((k, i) => (
                    <li key={i} className="flex items-start gap-2 font-mono text-[12px] text-cream leading-relaxed">
                      <span className="mt-1 w-1.5 h-1.5 rounded-full bg-gold shrink-0" /> {k}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {result.action_items?.length > 0 && (
              <div className="bg-emerald-500/10 border-2 border-emerald-500/40 rounded-2xl p-3.5" data-testid="doc-reader-actions">
                <div className="flex items-center gap-1.5 mb-2">
                  <CheckCircle2 size={14} className="text-emerald-400" />
                  <span className="font-mono text-[9px] font-black uppercase tracking-widest text-emerald-400">What to do</span>
                </div>
                <ul className="space-y-2">
                  {result.action_items.map((a, i) => (
                    <li key={i} className="flex items-start gap-2 font-mono text-[12px] text-cream leading-relaxed">
                      <CheckCircle2 size={13} className="text-emerald-400 mt-0.5 shrink-0" /> {a}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <p className="text-center font-mono text-[8px] uppercase tracking-widest text-white/30 pt-1">
              AI summary · verify important figures against the original
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
